/**
 * Derives the EV write-back chip label and tailwind colour class from the
 * order's `status.ev_write_back_status`.
 *
 * Memoisation depends only on the status string (not the entire
 * generalOrderDetails object), so unrelated order updates do not invalidate
 * the chip.
 *
 * @module busniessOffice/components/singleComment/hooks/useEvWriteBackStatus
 */

import { useMemo } from "react";
import { EVWriteBackStatus } from "../../../../../../../enums/evBvWriteBackStatus";
import { EvWriteBackStatusLabel } from "../types";

/**
 * @param generalOrderDetails - Order details (may be partial during loading).
 * @returns `{ label, color }` for the EV write-back chip.
 */
export const useEvWriteBackStatus = (
  writeStatus?: string,
): { label: EvWriteBackStatusLabel; color: string } => {
  const status = writeStatus ?? "";

  return useMemo(() => {
    if (
      status === EVWriteBackStatus.IN_PROGRESS ||
      status === EVWriteBackStatus.QUEUED
    ) {
      return { label: "Initiated", color: "text-secondaryYellow-4" };
    }
    if (status === EVWriteBackStatus.SUCCESS) {
      return { label: "Completed", color: "text-primaryGreen-4" };
    }
    if (status === EVWriteBackStatus.ERROR) {
      return { label: "Error", color: "text-tertiaryRed-4" };
    }
    return { label: "", color: "" };
  }, [status]);
};
